import { useEffect, useRef } from "react";

const SITE_KEY = "0x4AAAAAAEem-_JCKmXQkIK3";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window { turnstile?: TurnstileApi; }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`);
    const script = existing ?? document.createElement("script");
    const complete = () => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not initialize."));
    script.addEventListener("load", complete, { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile could not load.")), { once: true });
    if (!existing) {
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  });
  return scriptPromise;
}

export function TurnstileWidget({ action, onToken, onError }: {
  action: "login" | "signup";
  onToken: (token: string) => void;
  onError: (message: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    let widgetId: string | null = null;
    onToken("");
    void loadTurnstile().then((turnstile) => {
      if (!active || !containerRef.current) return;
      widgetId = turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        action,
        theme: "light",
        size: "flexible",
        callback: (token: string) => active && onToken(token),
        "expired-callback": () => active && onToken(""),
        "timeout-callback": () => active && onToken(""),
        "error-callback": () => active && onError("The security check needs a refresh."),
      });
    }).catch((caught: unknown) => {
      if (active) onError(caught instanceof Error ? caught.message : "The security check could not load.");
    });
    return () => {
      active = false;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [action, onError, onToken]);

  return <div className="turnstile-slot" ref={containerRef} aria-label="Security check" />;
}
